import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import SeoHead, { SITE_URL } from '@/components/feature/SeoHead';

const HERO_IMG = 'https://readdy.ai/api/search-image?query=Modern%20fertility%20clinic%20interior%20with%20warm%20champagne%20gold%20lighting%2C%20professional%20medical%20hallway%2C%20clean%20white%20and%20gold%20tones%2C%20soft%20natural%20light%2C%20wide%20angle%20architectural%20photography%2C%20hopeful%20atmosphere%2C%20medical%20equipment%20in%20background&width=1600&height=600&seq=svc-hero-2026&orientation=landscape';

const IVF_IMG = 'https://readdy.ai/api/search-image?query=IVF%20embryology%20laboratory%20scientist%20with%20microscope%2C%20clean%20modern%20medical%20research%20lab%2C%20warm%20white%20and%20champagne%20tones%2C%20professional%20embryologist%20examining%20embryos%2C%20high-tech%20fertility%20equipment%2C%20scientific%20precision%2C%20soft%20lighting&width=800&height=500&seq=svc-pg-ivf-2026&orientation=landscape';
const SURROGACY_IMG = 'https://readdy.ai/api/search-image?query=Surrogacy%20consultation%20caring%20doctor%20with%20couple%20in%20modern%20fertility%20clinic%2C%20warm%20champagne%20gold%20tones%2C%20professional%20medical%20team%2C%20soft%20natural%20light%2C%20hopeful%20emotional%20atmosphere%2C%20white%20medical%20interior&width=800&height=500&seq=svc-pg-sur-2026&orientation=landscape';
const FULL_IMG = 'https://readdy.ai/api/search-image?query=Complete%20fertility%20surrogacy%20service%20family%20with%20newborn%20baby%2C%20warm%20hospital%20nursery%20room%2C%20champagne%20gold%20soft%20lighting%2C%20emotional%20joyful%20moment%20parents%20holding%20baby%2C%20professional%20medical%20care%2C%20happy%20family%20atmosphere&width=800&height=500&seq=svc-pg-full-2026&orientation=landscape';

const servicesJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: '服务项目 - RTB吉尔吉斯斯坦试管代孕中心',
    description: '提供三代试管、单助孕、试管助孕三大核心服务，专业医疗团队全程护航。',
    url: `${SITE_URL}/services`,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'RTB吉尔吉斯斯坦试管代孕中心服务列表',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '三代试管', url: `${SITE_URL}/ivf` },
      { '@type': 'ListItem', position: 2, name: '单助孕', url: `${SITE_URL}/surrogacy` },
      { '@type': 'ListItem', position: 3, name: '试管助孕', url: `${SITE_URL}/fertility` },
    ],
  },
];

export default function ServicesPage() {
  const { t } = useTranslation();

  const services = useMemo(() => [
    {
      key: 'ivf',
      href: '/ivf',
      img: IVF_IMG,
      icon: 'ri-dna-line',
      title: t('svc_ivf_title'),
      subtitle: t('svc_ivf_subtitle'),
      desc: t('svc_ivf_desc'),
      features: [t('svc_ivf_feature1'), t('svc_ivf_feature2'), t('svc_ivf_feature3'), t('svc_ivf_feature4'), t('svc_ivf_feature5'), t('svc_ivf_feature6')],
    },
    {
      key: 'surrogacy',
      href: '/surrogacy',
      img: SURROGACY_IMG,
      icon: 'ri-heart-pulse-line',
      title: t('svc_surrogacy_title'),
      subtitle: t('svc_surrogacy_subtitle'),
      desc: t('svc_surrogacy_desc'),
      features: [t('svc_surrogacy_feature1'), t('svc_surrogacy_feature2'), t('svc_surrogacy_feature3'), t('svc_surrogacy_feature4'), t('svc_surrogacy_feature5'), t('svc_surrogacy_feature6')],
    },
    {
      key: 'full',
      href: '/fertility',
      img: FULL_IMG,
      icon: 'ri-shield-check-line',
      title: t('svc_full_title'),
      subtitle: t('svc_full_subtitle'),
      desc: t('svc_full_desc'),
      features: [t('svc_full_feature1'), t('svc_full_feature2'), t('svc_full_feature3'), t('svc_full_feature4'), t('svc_full_feature5'), t('svc_full_feature6')],
    },
  ], [t]);

  return (
    <>
      <SeoHead
        title="服务项目｜三代试管·单助孕·试管助孕 - RTB吉尔吉斯斯坦试管代孕中心"
        description="RTB吉尔吉斯斯坦试管代孕中心提供三代试管、单助孕、试管助孕三大核心服务，PGT-A基因筛查、代孕志愿者全程监护、法律双亲权保障，费用透明。"
        keywords="三代试管,单助孕,试管助孕,PGT-A基因筛查,吉尔吉斯斯坦代孕"
        canonical="/services"
        jsonLd={servicesJsonLd}
      />
      <main className="pt-16 md:pt-20">
        {/* Hero */}
        <section className="relative h-56 md:h-80 flex items-center overflow-hidden">
          <img src={HERO_IMG} alt="服务项目" className="absolute inset-0 w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-[#0d2137]/70"></div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 w-full">
            <div className="text-[#c9a96e] text-sm font-medium tracking-widest uppercase mb-2">{t('svc_section_label')}</div>
            <h1 className="text-3xl md:text-5xl font-bold text-white">{t('nav_services')}</h1>
          </div>
        </section>

        {/* Intro */}
        <section className="py-12 bg-[#f8f5f0]">
          <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
            <p className="text-gray-600 text-base leading-relaxed">
              {t('svc_intro')}
            </p>
          </div>
        </section>

        {/* Services list */}
        <section className="py-10 md:py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8 space-y-16">
            {services.map((svc, i) => (
              <article key={svc.key} className={`grid grid-cols-1 lg:grid-cols-2 gap-10 items-center ${i % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                <div className={i % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="w-full h-72 md:h-80 rounded-2xl overflow-hidden">
                    <img src={svc.img} alt={svc.title} className="w-full h-full object-cover object-top" />
                  </div>
                </div>
                <div className={i % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#1a3a5c]/8 text-[#c9a96e]">
                      <i className={`${svc.icon} text-xl`}></i>
                    </div>
                    <div>
                      <div className="font-bold text-[#1a3a5c] text-xl">{svc.title}</div>
                      <div className="text-gray-400 text-xs">{svc.subtitle}</div>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed mb-6">{svc.desc}</p>
                  <div className="grid grid-cols-2 gap-2 mb-6">
                    {svc.features.map((f) => (
                      <div key={f} className="flex items-center gap-2 text-sm text-gray-600">
                        <i className="ri-checkbox-circle-fill text-[#c9a96e] shrink-0"></i>
                        {f}
                      </div>
                    ))}
                  </div>
                  <Link
                    to={svc.href}
                    className="inline-flex items-center gap-2 bg-[#1a3a5c] hover:bg-[#2a5a8c] text-white text-sm font-medium px-6 py-2.5 rounded-full transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {t('service_more')} <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#f8f5f0]">
          <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1a3a5c] mb-4">{t('svc_cta_title')}</h2>
            <p className="text-gray-500 text-sm mb-8">{t('svc_cta_desc')}</p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-[#c9a96e] hover:bg-[#b8944f] text-white font-semibold px-8 py-3.5 rounded-full transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-customer-service-line"></i>
              {t('nav_consult')}
            </Link>
          </div>
        </section>
      </main>
    </>
  );
}